import { Res, InvoiceParamsFromPosDTO, PaymentDTO, DocumentType, Permission, SaleDTO, BuyerCustomerDTO, CustomerDetailsDTO } from 'core/api';
import { Result, CustomError } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { CashdrawerFacade, InvoiceFacade, OrderingFacade } from 'pos/facades';
import { WhenPrintService } from 'pos/services';
import { Command } from 'pos/common';
import { ClosingPaymentsStatus } from '../payments-processing';
import { Message } from 'core/ui-next/modal';


export type ProcessPaymentResult = Result<SaleDTO, 'preconditions-failed' | 'creation-aborted' | 'print-failed' | 'commit-cash-payments-failed'>;

export type Discounts = {
	discountPercentage: number,
	discountPercentageInCash: number,
	cashDiscount: number
};

type DocumentData = {
	ticketGlobalId: Nullable<string>,
	customer: BuyerCustomerDTO,
	discounts?: Discounts, // Si no se especifica no se pintan los descuentos
	pendingAmount: number
};

type InvoiceId = { invoiceId: number, invoiceType: DocumentType };
type TicketIds = { ticketIds: number[] };

export type InvoiceDocument = InvoiceId & DocumentData;
export type TicketDocument = TicketIds & DocumentData;

export type Document = InvoiceDocument | TicketDocument;

function isInvoice(document: InvoiceDocument | TicketDocument): document is InvoiceDocument {
	return (document as InvoiceDocument).invoiceId !== undefined;
}

function createCloseParams(invoiceType: DocumentType, payments: PaymentDTO[], printOnRemote: boolean, printGift: boolean, customer: BuyerCustomerDTO): InvoiceParamsFromPosDTO {

	const closeParams: InvoiceParamsFromPosDTO = {
		InvoiceType: invoiceType,
		PrintTicket: printOnRemote,
		Payments: payments,
		PrintGiftTicket: printGift,
		RelatedInvoiceId: null,
		BuyerCustomer: customer
	};

	return closeParams;
}

type CreateParams = {
	invoiceType: DocumentType,
	getDocument: () => Document
	setCustomer: (customer: BuyerCustomerDTO) => Promise<void>,
	payments: PaymentDTO[],
	printGift: boolean,
	commitCashPayments: () => Promise<Result>;
	closeDocument: (params: InvoiceParamsFromPosDTO) => Promise<Result<SaleDTO, CustomError>>;
};

const getCustomer = async (customer: BuyerCustomerDTO): Promise<Result<CustomerDetailsDTO>> => {
	return Command.execute('searchCustomerByText', customer.FiscalName);
};

export async function create(params: CreateParams): Promise<ProcessPaymentResult> {

	const { invoiceType, getDocument, setCustomer, payments, commitCashPayments, closeDocument, printGift } = params;

	const hasPermissionToCloseTicket = Preconditions.ensure(invoiceType === DocumentType.BasicInvoice ? Permission.Ticket_Close : Permission.Ticket_Invoice);

	if (!hasPermissionToCloseTicket())
		return Result.error('preconditions-failed');

	const hasPermissionToPrintGift = Preconditions.ensure(Permission.Ticket_PrintGift);
	if (printGift && !hasPermissionToPrintGift())
		return Result.error('preconditions-failed');


	let document = getDocument();

	// Si tengo cliente y lo cambian, lo actualizo
	if (document.customer.Id !== 0 && [DocumentType.StandardInvoice, DocumentType.DeliveryNote, DocumentType.SalesOrder].includes(invoiceType)) {

		const result = await getCustomer(document.customer);
		if (!result.ok)
			return Result.error('creation-aborted');

		if (document.customer.Id !== result.value.Id) {
			await setCustomer(result.value);
		}
	}

	// Si no tengo cliente lo asigno
	if (invoiceType === DocumentType.StandardInvoice && getDocument().customer.Id === 0) {
		await Command.execute('setOrRemoveCustomer', {
			customer: getDocument().customer,
			requiredPermission: Permission.Ticket_SetBuyerCustomer,
			setCustomer
		});

		if (getDocument().customer.Id === 0) {
			await Message.show(Res.Msg.MSG_CANNOT_EMIT_STANDARD_INVOICE_WITHOUT_CUSTOMER());
			return Result.error('preconditions-failed');
		}
	}

	document = getDocument();

	if (isInvoice(document))
		throw new Error('Sólo se pueden crear facturas desde un documento de tipo Ticket');

	if (!await ClosingPaymentsStatus.canPay(params.payments, document.pendingAmount))
		return Result.error('preconditions-failed');

	const checkResult = await OrderingFacade.checkRequiredCustomerForTickets(document.ticketIds, params.payments);
	if (!checkResult.IsValid) {
		await Message.show(checkResult.ErrorMessage);
		return Result.error('preconditions-failed');
	}

	return processPaymentsAndPrintDocument({ documentType: invoiceType, printGift, customer: document.customer, commitCashPayments, payments, process: closeDocument });
}

type ChangePaymentParams = {
	getDocument: () => Document,
	payments: PaymentDTO[],
	printGift: boolean,
	commitCashPayments: () => Promise<Result>;
	changePayments: (params: InvoiceParamsFromPosDTO) => Promise<Result<SaleDTO, CustomError>>;
};

export async function changePayment(params: ChangePaymentParams): Promise<ProcessPaymentResult> {

	const { getDocument, payments, changePayments, commitCashPayments, printGift } = params;

	const hasPermissiontoChangePayment = Preconditions.ensure(Permission.Ticket_ChangePayment);

	if (!hasPermissiontoChangePayment())
		return Result.error('preconditions-failed');

	const hasPermissionToPrintGift = Preconditions.ensure(Permission.Ticket_PrintGift);
	if (printGift && !hasPermissionToPrintGift())
		return Result.error('preconditions-failed');

	const document = getDocument();

	if (!isInvoice(document))
		throw new Error('Sólo se pueden cambiar las formas de pago de las facturas');

	const isRefund = document.invoiceType === DocumentType.BasicRefund || document.invoiceType === DocumentType.StandardRefund;
	if (isRefund) {
		await Message.show(Res.Msg.MSG_INVALID_OPERATION_FOR_REFUND());
		return Result.error('preconditions-failed');
	}

	const canChangeResult = await CashdrawerFacade.canChangeInvoicePayment(document.invoiceId);
	if (!canChangeResult.CanChange) {
		await Message.show(canChangeResult.Reason);
		return Result.error('preconditions-failed');
	}

	if (!await ClosingPaymentsStatus.canPay(payments, document.pendingAmount))
		return Result.error('preconditions-failed');

	const checkResult = await InvoiceFacade.checkRequiredCustomer(document.invoiceId, params.payments);
	if (!checkResult.IsValid) {
		await Message.show(checkResult.ErrorMessage);
		return Result.error('preconditions-failed');
	}

	return processPaymentsAndPrintDocument({ documentType: document.invoiceType, customer: document.customer, commitCashPayments, printGift, payments, process: changePayments });
}


type RefundParams = {
	invoiceType: DocumentType,
	getDocument: () => Document,
	setCustomer: (customer: BuyerCustomerDTO) => Promise<void>,
	payments: PaymentDTO[],
	commitCashPayments: () => Promise<Result>;
	refund: (params: InvoiceParamsFromPosDTO) => Promise<Result<SaleDTO, CustomError>>;
};

export async function refund(params: RefundParams): Promise<ProcessPaymentResult> {

	const { getDocument, payments, refund, invoiceType, commitCashPayments, setCustomer } = params;

	const hasPermissions = Preconditions.ensure(Permission.Ticket_Refund);

	if (!hasPermissions())
		return Result.error('preconditions-failed');


	if (invoiceType === DocumentType.StandardInvoice && getDocument().customer.Id === 0) {
		await Command.execute('setOrRemoveCustomer', {
			customer: getDocument().customer,
			requiredPermission: Permission.Ticket_SetBuyerCustomer,
			setCustomer: async (customer: BuyerCustomerDTO) => {
				if (customer.Cif === '') {
					await Message.show(Res.Msg.MSG_CANNOT_EMIT_STANDARD_INVOICE_WITH_CUSTOMER_WITH_EMPTY_CIF());
					return;
				}

				await setCustomer(customer);
			}
		});
		if (getDocument().customer.Id === 0) {
			await Message.show(Res.Msg.MSG_CANNOT_EMIT_STANDARD_INVOICE_WITHOUT_CUSTOMER());
			return Result.error('preconditions-failed');
		}
	}

	const document = getDocument();

	if (!await ClosingPaymentsStatus.canPay(payments, document.pendingAmount, true))
		return Result.error('preconditions-failed');

	if (isInvoice(document)) {
		const checkResult = await InvoiceFacade.checkRequiredCustomer(document.invoiceId, params.payments);
		if (!checkResult.IsValid) {
			await Message.show(checkResult.ErrorMessage);
			return Result.error('preconditions-failed');
		}
	}

	return processPaymentsAndPrintDocument({ documentType: invoiceType, printGift: false, customer: document.customer, commitCashPayments, payments, process: refund });
}

type ProcessPaymentParams = {
	documentType: DocumentType;
	printGift: boolean;
	payments: PaymentDTO[];
	customer: BuyerCustomerDTO;
	commitCashPayments: () => Promise<Result>;
	process: (params: InvoiceParamsFromPosDTO) => Promise<Result<SaleDTO, CustomError>>;
};

async function processPaymentsAndPrintDocument({ documentType, printGift, payments, customer, process, commitCashPayments }: ProcessPaymentParams): Promise<ProcessPaymentResult> {

	const shouldPrintOnRemote = await WhenPrintService.shouldPrintInvoice(documentType);
	const closeParams = createCloseParams(documentType, payments, shouldPrintOnRemote, printGift, customer);

	const commitResult = await commitCashPayments();
	if (!commitResult.ok)
		return Result.error('commit-cash-payments-failed');

	const result = await process(closeParams);

	if (result.ok)
		return result;

	if (result.error.isPrintError())
		return Result.error('print-failed');

	return Result.error('creation-aborted');
}